import React from "react";
import { css } from "@emotion/css";

import type { BottomBarState } from "../../types/state";

/**
 * The server-declared controls for the work lane — in practice, the way
 * on from a finished instrument.
 *
 * It is the HOST that draws this, and that is the point.  An activity
 * can only see its own contents, so a "next" it drew itself would be
 * answering "what should this learner meet now?" with the only thing it
 * knows: its own reading order.  The crafter picks out of everything the
 * learner could be doing, so the crafter's host owns the control.
 *
 * The other half of the same decision is that the route does not move
 * until this is pressed.  The serve seam reports only that the served
 * node is FINISHED; advancing on the close that carried the learner's
 * answer used to unmount the reveal they were about to read.  So the
 * activity keeps its payoff on screen for exactly as long as the learner
 * wants it, and this bar is how they say they are done with it.
 */

const barStyle = css`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid var(--color-border-subtle);
  background: color-mix(in srgb, var(--color-bg) 90%, transparent);
  backdrop-filter: blur(6px);
`;

const slotStyle = css`
  margin-right: auto;
`;

/* The house primary button.  Every colour here is a token theme.css
   defines — an invented name would not fail, it would silently fall
   back: `--color-accent` once painted this label in `--color-bg`, dark
   on dark, and the way on was invisible.  The pair is checked in the
   browser, in both schemes, by `e2e/test_runtime_house_styling.py`. */
const buttonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-on-primary);
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface SessionBottomBarProps {
  /** Nullable because a session stored before the bar existed has none. */
  bottomBar: BottomBarState | null;
  onAction: (actionId: string) => void;
}

export const SessionBottomBar: React.FC<SessionBottomBarProps> = ({ bottomBar, onAction }) => {
  const actions = bottomBar?.actions ?? [];
  if (actions.length === 0) return null;

  return (
    <div className={barStyle} data-testid="session-bottom-bar">
      {actions.map((action) => (
        <button
          key={action.buttonId}
          type="button"
          className={`${buttonStyle} ${action.slot === "left" ? slotStyle : ""}`}
          data-testid={`bottom-bar-${action.buttonId}`}
          disabled={action.disabled}
          onClick={() => onAction(action.actionId)}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
};
